import React, { useEffect, useState } from "react";
import api from "../../../api/axios";

/* The GST return's figures, from the tax documents themselves: posted
   invoices and credit notes, counter and app, by the date each was issued.
   Every amount is as posting froze it, so the report does not move when a
   rate in the tax master changes later. */

/* Every amount in the report is an integer number of PAISE. */
const rupees = (paise) => (Number(paise) || 0) / 100;

const fmt = (n) =>
  (Number(n) || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

/* first + last day of the previous month — the range you file for */
const prevMonthRange = () => {
  const now = new Date();
  const first = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const last = new Date(now.getFullYear(), now.getMonth(), 0);
  const ymd = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
      d.getDate()
    ).padStart(2, "0")}`;
  return { from: ymd(first), to: ymd(last) };
};

const LABEL = {
  display: "block",
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: 1,
  color: "var(--mly-text-muted)",
  marginBottom: 6,
  textTransform: "uppercase",
};
const INPUT = {
  padding: "9px 12px",
  borderRadius: 10,
  border: "1.5px solid var(--mly-border)",
  fontSize: 14,
  outline: "none",
  background: "var(--mly-surface)",
};

/* Which return table a rate row belongs to. B2B is Table 4; consumer sales
   at a positive rate are Table 7; 0% is Table 8 (nil rated / exempt). */
const rateLabel = (r) => {
  const pct = `${r.rateBp / 100}%`;
  if (r.section === "b2b") return `B2B ${pct} · Table 4`;
  return r.rateBp === 0 ? "0% · Table 8 (nil rated / exempt)" : `B2C ${pct} · Table 7`;
};

const money = (paise) => `₹${fmt(rupees(paise))}`;
const R = { textAlign: "right" };

const Section = ({ title, hint, children }) => (
  <div style={{ marginTop: 26 }}>
    <h6 style={{ fontWeight: 700, marginBottom: 2 }}>{title}</h6>
    {hint && <div style={{ fontSize: 12, color: "var(--mly-text-muted)", marginBottom: 8 }}>{hint}</div>}
    <div style={{ overflowX: "auto" }}>{children}</div>
  </div>
);

const Empty = ({ cols, text }) => (
  <tr>
    <td colSpan={cols} style={{ textAlign: "center", padding: "24px 0", color: "var(--mly-text-muted)" }}>{text}</td>
  </tr>
);

const GstReport = () => {
  const initial = prevMonthRange();
  const [from, setFrom] = useState(initial.from);
  const [to, setTo] = useState(initial.to);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = async (f = from, t = to) => {
    setLoading(true);
    setError("");
    try {
      /* from/to are required YYYY-MM-DD days on the store's clock; `to` is
         inclusive. The figures come from posted invoices and credit notes by
         the date they were issued -- till and app alike. */
      const res = await api.get(`/admin/orders/gst-report?from=${f}&to=${t}`);
      setData(res.data);
    } catch (e) {
      setError(e.response?.data?.message || e.response?.data?.code || "Failed to load the GST report");
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(initial.from, initial.to);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sum = (rows, key) => rows.reduce((n, r) => n + (Number(r[key]) || 0), 0);
  const rates = data?.rates || [];
  const netTax = sum(rates, "netCgstPaise") + sum(rates, "netSgstPaise") + sum(rates, "netIgstPaise");
  const noHsn = (data?.hsn || []).filter((h) => !h.hsn);

  const downloadCsv = () => {
    if (!data) return;
    const r2 = (p) => rupees(p).toFixed(2);
    const q = (s) => `"${String(s ?? "").replace(/"/g, '""')}"`;
    const lines = [
      `GST Report,${q(data.storeName)}`,
      `GSTIN,${data.gstin || "(set in Store Settings)"}`,
      `Period,${data.from} to ${data.to}`,
      "",
      "RATE-WISE (net of credit notes)",
      "Section,Rate %,Invoiced taxable,Credit notes taxable,Net taxable,Net CGST,Net SGST,Net IGST",
      ...rates.map((r) => [r.section.toUpperCase(), r.rateBp / 100, r2(r.invoiceTaxablePaise), r2(r.creditTaxablePaise),
        r2(r.netTaxablePaise), r2(r.netCgstPaise), r2(r.netSgstPaise), r2(r.netIgstPaise)].join(",")),
      "",
      "HSN SUMMARY (Table 12)",
      "Section,HSN,UQC,Rate %,Quantity,Taxable,CGST,SGST,IGST",
      ...data.hsn.map((h) => [h.section.toUpperCase(), h.hsn || "(none)", h.uqc, h.rateBp / 100, h.qty,
        r2(h.taxablePaise), r2(h.cgstPaise), r2(h.sgstPaise), r2(h.igstPaise)].join(",")),
      "",
      "B2B DOCUMENTS (Tables 4 and 9B)",
      "Type,Number,Date,Buyer GSTIN,Buyer,Document value,Against invoice,Rate %,Taxable,CGST,SGST,IGST",
      ...data.b2b.map((b) => [b.docType, b.docNo, String(b.issuedAt).slice(0, 10), b.buyerGstin, q(b.buyerName),
        r2(b.documentPaise), b.ofDocNo || "", b.rateBp / 100, r2(b.taxablePaise), r2(b.cgstPaise),
        r2(b.sgstPaise), r2(b.igstPaise)].join(",")),
      "",
      "DOCUMENTS ISSUED (Table 13)",
      "Type,Series,From,To,Issued,Cancelled",
      ...data.documents.map((d) => [d.docType, d.series, d.firstNo, d.lastNo, d.issued, d.cancelled].join(",")),
      "",
      `Note,${q(data.note)}`,
    ];
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `gst-report-${data.from}-to-${data.to}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div className="container-fluid">
      <div className="card" style={{ minHeight: "calc(100vh - 72px)" }}>
        <div className="card-header d-flex justify-content-between align-items-center flex-wrap" style={{ gap: 12 }}>
          <div>
            <h5 style={{ margin: 0, fontWeight: 700 }}>GST Report</h5>
            <span style={{ fontSize: 12, color: "var(--mly-text-muted)" }}>
              For GSTR-1: posted tax invoices and credit notes, counter and app, by the date issued.
            </span>
          </div>

          <div style={{ display: "flex", alignItems: "flex-end", gap: 10, flexWrap: "wrap" }}>
            <div>
              <label style={LABEL}>From</label>
              <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} style={INPUT} />
            </div>
            <div>
              <label style={LABEL}>To</label>
              <input type="date" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} style={INPUT} />
            </div>
            <button className="btn btn-primary btn-sm" style={{ height: 40 }} onClick={() => load()}>
              Run
            </button>
            <button className="btn btn-outline-secondary btn-sm" style={{ height: 40 }} onClick={downloadCsv}
                    disabled={!data || !rates.length}>
              ⬇ CSV
            </button>
          </div>
        </div>

        <div className="card-body">
          {loading ? (
            <div className="text-center py-4">Loading…</div>
          ) : error ? (
            <div className="text-danger text-center py-4">{error}</div>
          ) : !data ? null : (
            <>
              <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 6 }}>
                {[
                  { k: "GSTIN", v: data.gstin || "— set in Store Settings" },
                  { k: "Period", v: `${data.from} → ${data.to}` },
                  { k: "Net taxable", v: money(sum(rates, "netTaxablePaise")) },
                  { k: "Net tax", v: money(netTax) },
                ].map((c) => (
                  <div key={c.k} style={{
                    background: "var(--mly-surface-2)", border: "1px solid var(--mly-border)",
                    borderRadius: 12, padding: "10px 16px", minWidth: 130,
                  }}>
                    <div style={{ fontSize: 11, color: "var(--mly-text-muted)", fontWeight: 700, letterSpacing: 0.6, textTransform: "uppercase" }}>{c.k}</div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: "var(--mly-heading)", marginTop: 2 }}>{c.v}</div>
                  </div>
                ))}
              </div>

              {noHsn.length > 0 && (
                <div style={{ marginTop: 12, padding: "10px 14px", borderRadius: 10, background: "#fff7e6", color: "#854f0b", fontSize: 13 }}>
                  {money(sum(noHsn, "taxablePaise"))} of supplies have no HSN code. Set the HSN on those
                  products, and the codes for milled items and delivery in Store Settings → GST.
                </div>
              )}

              <Section title="Rate-wise, net of credit notes" hint="B2C at a positive rate goes to Table 7, 0% to Table 8, B2B to Table 4.">
                <table className="display table table-bordered">
                  <thead>
                    <tr>
                      <th>Rate</th>
                      <th style={R}>Invoiced taxable</th>
                      <th style={R}>Credit notes</th>
                      <th style={R}>Net taxable</th>
                      <th style={R}>CGST</th>
                      <th style={R}>SGST</th>
                      <th style={R}>IGST</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rates.length === 0 ? <Empty cols={7} text="No invoices issued in this period." /> : rates.map((r) => (
                      <tr key={`${r.section}-${r.rateBp}`}>
                        <td>{rateLabel(r)}</td>
                        <td style={R}>{money(r.invoiceTaxablePaise)}</td>
                        <td style={R}>{r.creditTaxablePaise ? `− ${money(r.creditTaxablePaise)}` : "—"}</td>
                        <td style={{ ...R, fontWeight: 600 }}>{money(r.netTaxablePaise)}</td>
                        <td style={R}>{money(r.netCgstPaise)}</td>
                        <td style={R}>{money(r.netSgstPaise)}</td>
                        <td style={R}>{money(r.netIgstPaise)}</td>
                      </tr>
                    ))}
                  </tbody>
                  {rates.length > 0 && (
                    <tfoot>
                      <tr style={{ fontWeight: 700, background: "var(--mly-surface-2)" }}>
                        <td>Total</td>
                        <td style={R}>{money(sum(rates, "invoiceTaxablePaise"))}</td>
                        <td style={R}>− {money(sum(rates, "creditTaxablePaise"))}</td>
                        <td style={R}>{money(sum(rates, "netTaxablePaise"))}</td>
                        <td style={R}>{money(sum(rates, "netCgstPaise"))}</td>
                        <td style={R}>{money(sum(rates, "netSgstPaise"))}</td>
                        <td style={R}>{money(sum(rates, "netIgstPaise"))}</td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </Section>

              <Section title="HSN summary · Table 12" hint="Quantity is what the invoices supplied; credit notes adjust value only. Services carry no quantity.">
                <table className="display table table-bordered">
                  <thead>
                    <tr>
                      <th>Section</th><th>HSN</th><th>UQC</th><th style={R}>Rate</th><th style={R}>Quantity</th>
                      <th style={R}>Taxable</th><th style={R}>CGST</th><th style={R}>SGST</th><th style={R}>IGST</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.hsn.length === 0 ? <Empty cols={9} text="Nothing to summarise." /> : data.hsn.map((h) => (
                      <tr key={`${h.section}-${h.hsn}-${h.uqc}-${h.rateBp}`}>
                        <td>{h.section.toUpperCase()}</td>
                        <td>{h.hsn || <span style={{ color: "#C62828", fontWeight: 600 }}>No HSN</span>}</td>
                        <td>{h.uqc}</td>
                        <td style={R}>{h.rateBp / 100}%</td>
                        <td style={R}>{Number(h.qty).toLocaleString("en-IN", { maximumFractionDigits: 3 })}</td>
                        <td style={R}>{money(h.taxablePaise)}</td>
                        <td style={R}>{money(h.cgstPaise)}</td>
                        <td style={R}>{money(h.sgstPaise)}</td>
                        <td style={R}>{money(h.igstPaise)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Section>

              <Section title="B2B documents · Tables 4 and 9B" hint="Invoices and credit notes to buyers who gave a GSTIN, rate by rate.">
                <table className="display table table-bordered">
                  <thead>
                    <tr>
                      <th>Type</th><th>Number</th><th>Date</th><th>Buyer GSTIN</th><th>Buyer</th>
                      <th style={R}>Value</th><th style={R}>Rate</th><th style={R}>Taxable</th><th style={R}>CGST</th><th style={R}>SGST</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.b2b.length === 0 ? <Empty cols={10} text="No B2B documents in this period." /> : data.b2b.map((b) => (
                      <tr key={`${b.docNo}-${b.rateBp}`}>
                        <td>{b.docType === "CRN" ? `Credit note (on ${b.ofDocNo})` : "Invoice"}</td>
                        <td>{b.docNo}</td>
                        <td>{String(b.issuedAt).slice(0, 10)}</td>
                        <td>{b.buyerGstin}</td>
                        <td>{b.buyerName}</td>
                        <td style={R}>{money(b.documentPaise)}</td>
                        <td style={R}>{b.rateBp / 100}%</td>
                        <td style={R}>{money(b.taxablePaise)}</td>
                        <td style={R}>{money(b.cgstPaise)}</td>
                        <td style={R}>{money(b.sgstPaise)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Section>

              <Section title="Documents issued · Table 13" hint="Every series used in the period. A cancelled number is reported, never reused.">
                <table className="display table table-bordered">
                  <thead>
                    <tr><th>Type</th><th>Series</th><th>From</th><th>To</th><th style={R}>Issued</th><th style={R}>Cancelled</th></tr>
                  </thead>
                  <tbody>
                    {data.documents.length === 0 ? <Empty cols={6} text="No documents issued." /> : data.documents.map((d) => (
                      <tr key={`${d.docType}-${d.series}`}>
                        <td>{d.docType === "CRN" ? "Credit notes" : "Invoices"}</td>
                        <td>{d.series}</td>
                        <td>{d.firstNo}</td>
                        <td>{d.lastNo}</td>
                        <td style={R}>{d.issued}</td>
                        <td style={R}>{d.cancelled}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Section>

              <div style={{ fontSize: 12, color: "var(--mly-text-muted)", marginTop: 12 }}>{data.note}</div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default GstReport;
